import type { ControlAction } from "@/lib/controlActions";
import { actionProps } from "@/lib/controlAnalytics";
import { AGE_CHECK } from "@/lib/consent";
import { ROUTES } from "@/lib/routes";

/** A legal page, in a new tab, so the sheet or the prompt stays where it is. */
function LegalLink({ to, source, children }: { to: string; source: string; children: string }) {
    return (
        <a
            {...actionProps("navigation_clicked", { source, target: to })}
            href={to}
            target="_blank"
            rel="noopener"
            className="font-semibold text-brand-ink underline underline-offset-2"
        >
            {children}
        </a>
    );
}

/**
 * The one check box a person ticks to agree: unticked to begin with, the
 * Terms and the Privacy policy linked in its words. `age` asks for the
 * age alone, for a person who accepted the terms but never confirmed it.
 * A press that went on without the tick shows `error` under it.
 */
export default function TermsCheck({
    id,
    ask = "terms",
    checked,
    error,
    action,
    source,
    onChange,
}: {
    id: string;
    ask?: "terms" | "age";
    checked: boolean;
    error?: string | null;
    action: ControlAction;
    source: string;
    onChange: (checked: boolean) => void;
}) {
    const errorId = `${id}-error`;
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-start gap-2.5 rounded-[12px] border border-line bg-card-raised px-3 py-2.5">
                <input
                    {...actionProps(action, { source, next_state: !checked })}
                    id={id}
                    type="checkbox"
                    checked={checked}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    onChange={(event) => onChange(event.target.checked)}
                    className="mt-0.5 size-[16px] shrink-0 accent-[#0f7a3a]"
                />
                {ask === "age" ? (
                    <label htmlFor={id} className="text-[13px] leading-[1.45] text-fg">
                        {AGE_CHECK}
                    </label>
                ) : (
                    // The words are TERMS_CHECK, with its two documents linked.
                    <label htmlFor={id} className="text-[13px] leading-[1.45] text-fg">
                        I am 18 or older and I agree to the{" "}
                        <LegalLink to={ROUTES.TERMS} source={source}>
                            Terms
                        </LegalLink>{" "}
                        and the{" "}
                        <LegalLink to={ROUTES.PRIVACY} source={source}>
                            Privacy policy
                        </LegalLink>
                    </label>
                )}
            </div>
            {error && (
                <p
                    id={errorId}
                    role="alert"
                    className="m-0 animate-fade-in text-[12.5px] font-medium text-negative"
                >
                    {error}
                </p>
            )}
        </div>
    );
}
